/**
 * Trade Value Gauge — Professional circular gauge for trade value scores
 */
import React from 'react';
import { Box, Typography, useTheme, alpha, Chip } from '@mui/material';
import { motion } from 'framer-motion';

const TRADE_TIERS = [
  { min: 85, label: 'MVP Caliber', color: '#FFD700', emoji: '💎' },
  { min: 75, label: 'All-Star', color: '#ff6b35', emoji: '⭐' },
  { min: 65, label: 'Quality Regular', color: '#4CAF50', emoji: '✅' },
  { min: 55, label: 'Solid Starter', color: '#2196F3', emoji: '👍' },
  { min: 45, label: 'Average', color: '#9E9E9E', emoji: '➖' },
  { min: 35, label: 'Below Average', color: '#795548', emoji: '📉' },
  { min: 25, label: 'Fringe', color: '#FF5722', emoji: '🔄' },
  { min: 0, label: 'Replacement', color: '#F44336', emoji: '📋' }
];

const getTradeTier = (value) => TRADE_TIERS.find(t => value >= t.min) || TRADE_TIERS[TRADE_TIERS.length - 1];

const TradeValueGauge = ({ value, surplusValue, compact = false }) => {
  const theme = useTheme();
  const tier = getTradeTier(value || 0);
  
  const size = compact ? 76 : 108;
  const strokeWidth = compact ? 5 : 7;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(100, value || 0) / 100;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <motion.div initial={{ opacity: 0, scale: 0.93 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        {/* Circular Gauge */}
        <Box sx={{ position: 'relative', width: size, height: size }}>
          <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={alpha(theme.palette.divider, 0.12)}
              strokeWidth={strokeWidth}
            />
            <motion.circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={tier.color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
              style={{ filter: `drop-shadow(0 0 4px ${alpha(tier.color, 0.3)})` }}
            />
          </svg>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)', textAlign: 'center',
          }}>
            <Typography variant={compact ? 'h6' : 'h5'} fontWeight={800} sx={{ color: tier.color, lineHeight: 1, fontSize: compact ? '1rem' : '1.4rem' }}>
              {(value || 0).toFixed(0)}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary' }}>
              TV
            </Typography>
          </Box>
        </Box>
        
        {/* Tier Badge */}
        <Box sx={{ textAlign: 'center' }}>
          <Chip
            label={`${tier.emoji} ${tier.label}`}
            size="small"
            sx={{
              bgcolor: alpha(tier.color, 0.1),
              color: tier.color,
              fontWeight: 700,
              fontSize: compact ? '0.75rem' : '0.8rem',
              border: `1px solid ${alpha(tier.color, 0.2)}`,
              height: 24,
            }}
          />
          {surplusValue !== undefined && !compact && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
              Surplus: ${typeof surplusValue === 'number' ? (surplusValue / 1000000).toFixed(1) + 'M' : '—'}
            </Typography>
          )}
        </Box>
      </Box>
    </motion.div>
  );
};

export default TradeValueGauge;
export { TRADE_TIERS, getTradeTier };
